import { BlobServiceClient } from '@azure/storage-blob';
import axios from 'axios';
import { Moment } from 'moment';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { isPresent, present } from '@/shared/utils/typescript';
import MediaFileType from '@/shared/constants/MediaFileType';
import { formatMoment } from '@/shared/converters/dateConverters';
import EntityType from '../constants/EntityType';
import { formatEntityData, parseMediaFiles2 } from '../converters/mediaFileConverters2';
import MediaFile2 from '../models/MediaFile2';
import { JobId } from '@/jobs/models/AbstractJob';
import { DiagnosticId } from '@/jobs/models/diagnosis/Diagnostic';
import { ModelID } from '@/shared/models/BaseModel';
import MediaTargetData from '@/general/models/MediaTargetData';
import { parseSlaV2Note } from '@/dossier/converters/slaV2Converters';

export interface SASData {
  name: string;
  blobUri: string;
  token: string;
  blobRelativePath: string;
  serviceEndpoint: string;
  container: string;
}

export interface BaseMediaData {
  relativePath: string;
  type: MediaFileType;
  isLocked: boolean;
  isWarranty: boolean;
}

export interface WheelMediaData extends BaseMediaData {
  subEntity: EntityType;
  subEntityId: ModelID;
}

export interface DossierMediaData extends BaseMediaData {
  description: string;
  date: Moment;
}

export interface SlaMediaFileRequestData {
    file: {
      blobRelativePath: string,
      type: MediaFileType
    } | null,
    attachmentDescription: string | null,
    subject: string
}

export type JobMediaData = BaseMediaData;
export type DiagnosticMediaData = Omit<BaseMediaData, 'isWarranty'>;

export default class MediaFileClient2 {
  static async loadAllForDossier(dossierId: DossierId): Promise<Array<MediaFile2>> {
    const response = await axios.get(`/api/mobopro/v2/dossiers/${dossierId}/media`);
    return parseMediaFiles2(response.data);
  }

  static async edit(mediaFiles: Array<MediaFile2>, isLocked?: boolean, hasWarranty?: boolean): Promise<void> {
    await axios.patch('/api/mobopro/v2/mediafiles', mediaFiles.map(mediaFile => {
      return {
        id: mediaFile.id,
        entityType: mediaFile.entityType,
        subEntityType: mediaFile.subEntityType,
        isLocked,
        isWarranty: hasWarranty,
      };
    }));
  }

  static async updateNote(file: MediaFile2, note: string): Promise<void> {
    await axios.patch('/api/mobopro/v2/mediafiles', [{
      id: present(file.id),
      entityType: present(file.entityType),
      subEntityType: file.subEntityType,
      description: note || null,
    }]);
  }

  static async remove(mediaFiles: Array<MediaFile2>): Promise<void> {
    const promises = mediaFiles.map(file => {
      return axios.delete(`/api/mobopro/v2/mediafiles?mediafileid=${file.id}&entitytype=${file.entityType}${isPresent(file.subEntityType) ? `&subentitytype=${file.subEntityType}` : ''}`);
    });

    await Promise.all(promises);
  }

  static async getSASData(dossierId: DossierId, targetData: MediaTargetData, fileNames: Array<string>): Promise<Array<SASData>> {
    const body = {
      ...formatEntityData(targetData),
      dossierId,
      fileNames,
      minutes: 10,
    };

    const response = await axios.post('/api/mobopro/v1/mediafiles/sas', body);
    return response.data;
  }

  static async uploadToAzure(sasData: SASData, file: File): Promise<void> {
    const blobServiceClient = new BlobServiceClient(sasData.serviceEndpoint + '?' + sasData.token);
    const containerClient = blobServiceClient.getContainerClient(sasData.container);
    const blockBlobClient = containerClient.getBlockBlobClient(sasData.blobRelativePath);

    const options = {
      blobHTTPHeaders: {
        blobContentType: file.type,
      },
    };

    await blockBlobClient.uploadData(file, options);
  }

  static async uploadBase64ToAzure(sasData: SASData, base64Image: string, fileName: string): Promise<void> {
    const byteCharacters = atob(present(base64Image.split(',').pop()));
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpeg' });

    await this.uploadToAzure(sasData, new File([blob], fileName));
  }

  static async registerWheelMedia(dossierId: DossierId, data: Array<WheelMediaData>): Promise<void> {
    await axios.post(
      `/api/mobopro/v2/dossiers/${dossierId}/wheels/medias`,
      data.map(d => {
        return {
          file: {
            blobRelativePath: d.relativePath,
            type: d.type,
          },
          isLocked: d.isLocked,
          isWarranty: d.isWarranty,
          wheelEntity: d.subEntity,
          wheelEntityId: d.subEntityId,
        };
      }),
      { skipLoader: true },
    );
  }

  static async registerDossierMedia(dossierId: DossierId, data: Array<DossierMediaData>): Promise<void> {
    await axios.post(
      `/api/mobopro/v2/dossiers/${dossierId}/attachments`,
      data.map(d => {
        return {
          file: {
            blobRelativePath: d.relativePath,
            type: d.type,
          },
          isLocked: d.isLocked,
          isWarranty: d.isWarranty,
          description: d.description,
          attachmentDate: formatMoment(d.date),
        };
      }),
      { skipLoader: true },
    );
  }

  static async registerJobMedia(dossierId: DossierId, jobId: JobId, data: Array<JobMediaData>): Promise<void> {
    await axios.post(
      `/api/mobopro/v2/dossiers/${dossierId}/jobs/${jobId}/medias`,
      data.map(d => {
        return {
          file: {
            blobRelativePath: d.relativePath,
            type: d.type,
          },
          isLocked: d.isLocked,
          isWarranty: d.isWarranty,
        };
      }),
      { skipLoader: true },
    );
  }

  static async registerDiagnosticMedia(dossierId: DossierId, jobId: JobId, diagnosticId: DiagnosticId, data: Array<DiagnosticMediaData>): Promise<void> {
    await axios.post(
      `/api/mobopro/v2/dossiers/${dossierId}/jobs/${jobId}/diagnostics/${diagnosticId}/medias`,
      data.map(d => {
        return {
          file: {
            blobRelativePath: d.relativePath,
            type: d.type,
          },
          isLocked: d.isLocked,
        };
      }),
      { skipLoader: true },
    );
  }

  static async registerSlaMedia(slaNoteId: number, filePath: string | null, attachmentDescription: string | null, subject: string, fileType: MediaFileType | null) {
    const body: SlaMediaFileRequestData = {
      file: null,
      attachmentDescription: null,
      subject,
    };

    if (filePath) {
      body.file = {
        blobRelativePath: filePath,
        type: present(fileType),
      };
      body.attachmentDescription = attachmentDescription;
    }

    const response = await axios.post(`/api/mobopro/v2/slanotes/${slaNoteId}/attachment`, body);
    return present(parseSlaV2Note(response.data));
  }

  static async registerDriveBookingMedia(driveBookingId: ModelID, data: Array<JobMediaData>): Promise<void> {
    await axios.post(
      `/api/mobopro/v1/drivebookings/${driveBookingId}/media`,
      data.map(d => {
        return {
          file: {
            blobRelativePath: d.relativePath,
            type: d.type,
          },
          isLocked: d.isLocked,
          isWarranty: d.isWarranty,
        };
      }),
      { skipLoader: true },
    );
  }
}